import { logger } from 'common/services';
import EventEmitter from 'events';
import { UrlNames } from 'modules/app/constants';
import { configService } from 'modules/app/services';

import { OpenNatterSocket, SocketEvents } from './socket/OpenNatterSocket';

/**
 * A stable EventEmitter proxy that forwards all events from the current
 * underlying OpenNatterSocket. The middleware attaches Redux-dispatching
 * listeners to this proxy once; when the real socket is replaced via
 * getSocket(id, userId), the proxy seamlessly switches to the new socket.
 *
 * The proxy also implements the OpenNatterSocket public API so it can be
 * used as a drop-in replacement by the middleware and listeners.
 */
class SocketProxy extends EventEmitter {
  private underlying: OpenNatterSocket | null = null;
  private readonly forwardHandlers = new Map<string, (...args: unknown[]) => void>();

  attachTo(socket: OpenNatterSocket): void {
    this.detach();
    this.underlying = socket;

    // Forward all known events from the new socket to this proxy
    const events = Object.values(SocketEvents) as string[];
    for (const event of events) {
      const handler = (...args: unknown[]) => {
        this.emit(event, ...args);
      };
      this.forwardHandlers.set(event, handler);
      socket.on(event, handler);
    }
  }

  private detach(): void {
    if (this.underlying) {
      for (const [event, handler] of this.forwardHandlers) {
        this.underlying.removeListener(event, handler);
      }
      this.forwardHandlers.clear();
      this.underlying = null;
    }
  }

  connect(): void {
    this.underlying?.connect();
  }

  disconnect(): void {
    this.underlying?.disconnect();
    this.detach();
  }

  subscribe(channels: { personal: string; broadcast: string }): void {
    this.underlying?.subscribe(channels);
  }

  unsubscribe(channels: { personal: string; broadcast: string }): void {
    this.underlying?.unsubscribe(channels);
  }

  isConnected(): boolean {
    return this.underlying?.isConnected() ?? false;
  }
}

/**
 * Open Natter Feature Module
 *
 * Provides lazy initialization for Open Natter services.
 * Services are only created when the feature is actually used,
 * preventing crashes when environment variables are missing.
 *
 * Usage:
 * ```typescript
 * // In listener — creates/replaces socket for the given natter+user:
 * const socket = openNatterFeature.getSocket(openNatterId, userId);
 *
 * // In middleware — returns the stable proxy socket (always safe):
 * const socket = openNatterFeature.getSocket();
 * ```
 */
class OpenNatterFeature {
  private realSocket: OpenNatterSocket | null = null;
  private readonly proxySocket = new SocketProxy();
  private currentOpenNatterId: string | null = null;
  private currentUserId: string | null = null;
  private initialized = false;

  /**
   * Initialize the feature
   * Validates environment variables and creates services
   * Throws if required configuration is missing
   */
  initialize(): void {
    if (this.initialized) return;

    // Validate required environment variables
    const apiUrl = configService.getUrl(UrlNames.OpenNatterApi);

    if (!apiUrl) {
      throw new Error('Open Natter feature requires VITE_APP_OPEN_NATTER_API_URL');
    }

    logger.info('[OpenNatterFeature] Initialized', { apiUrl });
    this.initialized = true;
  }

  /**
   * Check if the feature is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Get the Ably socket client.
   *
   * When called with (openNatterId, userId): creates a new socket for that natter+user,
   * closing the previous one if the args changed. The stable proxy is updated to
   * forward events from the new socket.
   *
   * When called with no args: returns the stable proxy socket. Safe to call even before
   * a real socket is created — the proxy acts as a no-op EventEmitter until a real
   * socket is attached via getSocket(id, userId).
   */
  getSocket(openNatterId?: string, userId?: string): OpenNatterSocket {
    if (openNatterId && userId) {
      const sameSocket =
        this.realSocket !== null && this.currentOpenNatterId === openNatterId && this.currentUserId === userId;

      if (!sameSocket) {
        // Close the old socket before creating a new one
        if (this.realSocket) {
          logger.debug('[OpenNatterFeature] Replacing socket', {
            previousId: this.currentOpenNatterId,
            newId: openNatterId,
          });
          this.realSocket.disconnect();
        }

        this.realSocket = new OpenNatterSocket({
          apiBaseUrl: this.getApiUrl(),
          openNatterId,
          userId,
        });
        this.currentOpenNatterId = openNatterId;
        this.currentUserId = userId;

        // Update the proxy to forward events from the new socket
        this.proxySocket.attachTo(this.realSocket);

        logger.debug('[OpenNatterFeature] Created new socket', { openNatterId, userId });
      }
    }

    // Always return the proxy — safe to call with no args before any socket is created
    return this.proxySocket as unknown as OpenNatterSocket;
  }

  /**
   * Get the API base URL
   */
  getApiUrl(): string {
    return configService.getUrl(UrlNames.OpenNatterApi);
  }
}

/**
 * Singleton instance
 */
export const openNatterFeature = new OpenNatterFeature();
